export const brand = {
  name: 'Atlas Visual de Instrumentación Quirúrgica',
  promise: 'Reconoce y diferencia +150 instrumentos quirúrgicos',
  promiseAccent: 'sin memorizar nombres sueltos',
  subtitle:
    'Estudia instrumental, especialidades, mesas quirúrgicas, suturas y bioseguridad con un único sistema visual.',
  system: 'Sistema Completo — 6 Tomos + 5 Bonos',
  heroCta: 'Ver el Atlas por dentro',
}

export const heroBadges: string[] = ['Acceso inmediato', 'PDF imprimible', 'Acceso de por vida']

export const primaryCta = 'Quiero acceder al Atlas completo'

export const checkoutNote = 'Pagá de forma segura con Mercado Pago o transferencia bancaria.'

export interface Stat {
  value: string
  label: string
}

export const statsTitle = 'Todo tu estudio de Instrumentación Quirúrgica, organizado visualmente'

export const stats: Stat[] = [
  { value: '+150', label: 'Instrumentos quirúrgicos' },
  { value: '+100', label: 'Ejercicios visuales' },
  { value: '150', label: 'Flashcards' },
  { value: '+10', label: 'Especialidades' },
  { value: '6', label: 'Tomos' },
  { value: '5', label: 'Bonos' },
]

export const painPoints: string[] = [
  'Ves dos pinzas similares y dudas cuál es cuál.',
  'Recuerdas el nombre, pero cuando ves el instrumento fuera de tus apuntes no lo reconoces.',
  'Tienes fotografías, capturas y PDFs dispersos y pierdes tiempo buscando información.',
  'Confundes dientes, estrías, curvaturas, mandíbulas o mecanismos entre instrumentos parecidos.',
  'Antes de una evaluación terminas intentando memorizar listas interminables de nombres.',
  'Sabes que Kelly, Crile, Kocher, Allis o Babcock son diferentes… pero todavía dudas qué detalle debes observar.',
]

export interface SolutionPoint {
  title: string
  description: string
}

export const solutionPoints: SolutionPoint[] = [
  {
    title: '+150 instrumentos organizados visualmente',
    description:
      'Deja de estudiar fotografías aisladas y aprende a reconocer formas, puntas, dientes, estrías y mecanismos.',
  },
  {
    title: 'Comparaciones lado a lado',
    description: 'Visualiza las diferencias entre instrumentos que suelen confundirse.',
  },
  {
    title: 'Organizado por familias',
    description:
      'Corte, hemostasia, prensión, separación, síntesis, aspiración, dilatación, laparoscopia y más.',
  },
  {
    title: 'Más que un atlas de instrumentos',
    description:
      'También estudiarás especialidades, mesas quirúrgicas, suturas, bioseguridad y organización del material.',
  },
  {
    title: 'Práctica visual',
    description:
      'Comprueba si realmente reconoces lo aprendido mediante ejercicios y flashcards.',
  },
]

export interface Volume {
  tag: string
  title: string
  paragraphs: string[]
  items?: string[]
  flow?: string[]
  closing?: string[]
  featured?: boolean
}

export const volumes: Volume[] = [
  {
    tag: 'Tomo 1',
    title: 'Atlas Visual de +150 Instrumentos Quirúrgicos',
    paragraphs: [
      'El núcleo del sistema.',
      'Cada instrumento incluye imagen, nombre, familia, función general, forma de reconocerlo, detalle clave y comparación cuando corresponde.',
      'Incluye instrumental de corte, hemostasia, prensión, separación, síntesis, aspiración, dilatación, instrumental óseo, laparoscopia y más.',
    ],
    featured: true,
  },
  {
    tag: 'Tomo 2',
    title: 'Instrumental por Especialidad Quirúrgica',
    paragraphs: ['Organiza el instrumental según las áreas donde suele encontrarse.'],
    items: [
      'Cirugía general',
      'Traumatología y ortopedia',
      'Ginecología y obstetricia',
      'Urología',
      'Cardiovascular',
      'Neurocirugía',
      'Otorrinolaringología',
      'Oftalmología',
      'Cirugía plástica',
      'Laparoscopia',
    ],
  },
  {
    tag: 'Tomo 3',
    title: 'Mesas Quirúrgicas y Organización del Instrumental',
    paragraphs: ['Aprende visualmente cómo se agrupa y organiza el material.'],
    items: [
      'Mesa principal',
      'Mesa auxiliar',
      'Distribución por familias',
      'Zonas de organización',
      'Secuencia de preparación',
      'Conteo y control',
      'Ejemplos visuales de mesas organizadas',
    ],
  },
  {
    tag: 'Tomo 4',
    title: 'Suturas, Agujas y Materiales de Síntesis',
    paragraphs: ['Una guía visual para comparar:'],
    items: [
      'Suturas absorbibles y no absorbibles',
      'Monofilamento y multifilamento',
      'Materiales y calibres',
      'Tipos de agujas',
      'Curvaturas y puntas',
      'Clips, grapadoras, mallas y drenajes',
    ],
  },
  {
    tag: 'Tomo 5',
    title: 'Bioseguridad, Esterilización y Manejo del Material',
    paragraphs: ['Visualiza el recorrido del material:'],
    flow: ['Contaminado', 'Limpieza', 'Preparación', 'Empaque', 'Esterilización', 'Almacenamiento'],
    closing: [
      'Incluye principios de asepsia, manipulación segura, indicadores, campo estéril, errores frecuentes y comparación de métodos de esterilización.',
    ],
  },
  {
    tag: 'Tomo 6',
    title: 'Cuaderno Visual de Práctica + Simulador',
    paragraphs: [
      'Comprueba si realmente puedes reconocer el instrumental con +100 ejercicios visuales.',
    ],
    items: [
      '¿Qué instrumento es?',
      '¿A qué familia pertenece?',
      'Encuentra la diferencia',
      'Relaciona instrumento con nombre',
      'Instrumento intruso',
      'Reconocimiento rápido',
      'Opción múltiple',
      'Comparaciones visuales',
    ],
    closing: ['Mira la imagen. Piensa la respuesta. Comprueba si lo reconoces.'],
    featured: true,
  },
]

export const fichaIncludes: string[] = [
  'Qué instrumento es',
  'A qué familia pertenece',
  'Cuál es su función general',
  'Cómo reconocerlo',
  'Qué detalle lo diferencia',
  'Con qué instrumento puede confundirse',
]

export interface GalleryItem {
  title: string
  caption: string
  image: string
}

export const gallery: GalleryItem[] = [
  {
    title: 'Instrumental óseo y traumatológico',
    caption:
      'Curetas, elevadores periósticos y pinza gubia, con los extremos activos que diferencian a cada instrumento.',
    image:
      'https://cdn.magicpatterns.com/uploads/avo3ohCoceKeb1Fxm8KRtv/Captura_de_pantalla_2026-09-26_a_la(s)_6.39.33_p._m..png',
  },
  {
    title: 'Aspiración e irrigación',
    caption:
      'Reconoce cada cánula por su calibre, extremo, mango e indicación en una sola referencia.',
    image:
      'https://cdn.magicpatterns.com/uploads/fS2LGgA9cMhnUCgCankbKk/Captura_de_pantalla_2026-09-26_a_la(s)_6.39.55_p._m..png',
  },
  {
    title: 'Allis vs Babcock',
    caption:
      'Observa directamente las mandíbulas, los dientes y la forma que diferencia a cada instrumento.',
    image:
      'https://cdn.magicpatterns.com/uploads/xzMLNMSVGZ6UCk6kL6QeFv/Captura_de_pantalla_2026-09-26_a_la(s)_6.40.54_p._m..png',
  },
  {
    title: 'Yankauer vs Poole vs Frazier',
    caption:
      'Compara calibre, extremo y mecanismo de las cánulas de aspiración que suelen confundirse.',
    image:
      'https://cdn.magicpatterns.com/uploads/wYETjonM9jMToEZ8ExTNf4/Captura_de_pantalla_2026-09-26_a_la(s)_6.41.23_p._m..png',
  },
  {
    title: 'Instrumental laparoscópico',
    caption:
      'Reconoce visualmente trocares, aguja de Veress, pinzas laparoscópicas, tijeras, portaagujas e instrumental de acceso.',
    image:
      'https://cdn.magicpatterns.com/uploads/ss5K1kp8B6y23PuqxHWzq4/Captura_de_pantalla_2026-09-27_a_la(s)_1.37.57_a._m..png',
  },
]

export const comparisonRows: { old: string; atlas: string }[] = [
  { old: 'Ves una pinza y dudas', atlas: 'Sabes qué detalle observar' },
  { old: 'Memorizas nombres', atlas: 'Reconoces características' },
  { old: 'Fotos dispersas', atlas: 'Todo organizado por familias' },
  { old: 'Confundes instrumentos similares', atlas: 'Los comparas lado a lado' },
  { old: 'Lees y relees', atlas: 'Practicas reconocimiento' },
  { old: 'Buscas en varios apuntes', atlas: 'Consultas un solo sistema' },
]

export interface Bonus {
  tag: string
  title: string
  value: string
  paragraphs: string[]
  items?: string[]
}

export const bonuses: Bonus[] = [
  {
    tag: 'Bono 1',
    title: '100 Instrumentos en 100 Minutos',
    value: '$7.990',
    paragraphs: [
      'Una guía express con los instrumentos esenciales para repasar rápidamente imagen, nombre, familia, función y detalle visual clave.',
    ],
  },
  {
    tag: 'Bono 2',
    title: 'Diccionario Visual de Instrumentación Quirúrgica',
    value: '$6.990',
    paragraphs: [
      'Más de 200 términos de instrumental, materiales, áreas quirúrgicas, bioseguridad, esterilización y terminología frecuente explicados de forma sencilla.',
    ],
  },
  {
    tag: 'Bono 3',
    title: '150 Flashcards Imprimibles',
    value: '$9.990',
    paragraphs: [
      'Practica reconocimiento activo.',
      'En el frente ves el instrumento.',
      'En el reverso encuentras su nombre, familia, función general y cómo reconocerlo.',
      'Úsalas impresas o desde tu dispositivo.',
    ],
  },
  {
    tag: 'Bono 4',
    title: 'Checklist de Preparación y Repaso Quirúrgico',
    value: '$4.990',
    paragraphs: [
      'Checklists para repasar instrumental, familias, mesas quirúrgicas, suturas, bioseguridad, especialidades y preparación para evaluaciones.',
    ],
  },
  {
    tag: 'Bono 5',
    title: 'Plan Visual de Estudio de 7 Días',
    value: '$5.990',
    paragraphs: ['Una ruta organizada para repasar:'],
    items: [
      'Día 1 — Familias e instrumental básico',
      'Día 2 — Corte, hemostasia y prensión',
      'Día 3 — Separación y síntesis',
      'Día 4 — Suturas y agujas',
      'Día 5 — Especialidades y mesas',
      'Día 6 — Bioseguridad + flashcards',
      'Día 7 — Simulador + repaso',
    ],
  },
]

export interface FaqItem {
  question: string
  answer: string
}

export const faqs: FaqItem[] = [
  {
    question: '¿Cuándo recibo el material?',
    answer:
      'El acceso es inmediato después de confirmar el pago. Podrás comenzar a estudiar desde tu dispositivo.',
  },
  {
    question: '¿Sirve si recién estoy comenzando?',
    answer:
      'Sí. El contenido parte de familias e instrumentos fundamentales y avanza hacia comparaciones, especialidades y práctica.',
  },
  {
    question: '¿Sirve si ya estoy avanzado o haciendo prácticas?',
    answer: 'Sí. También funciona como material de repaso y consulta visual rápida.',
  },
  {
    question: '¿Qué incluye exactamente?',
    answer:
      'Recibes 6 tomos, +150 instrumentos, +100 ejercicios, 150 flashcards, instrumental por especialidad, mesas quirúrgicas, suturas, bioseguridad y 5 bonos.',
  },
  {
    question: '¿Puedo verlo desde el celular o imprimirlo?',
    answer:
      'Sí. Puedes consultarlo desde celular, tablet o computadora y también imprimir los PDFs.',
  },
  {
    question: '¿El acceso es de por vida?',
    answer:
      'Sí. Es un pago único, sin suscripción mensual, con acceso de por vida y garantía de 7 días.',
  },
]

export const checkoutUrl = 'https://instrumentacion.impultienda.ar'

export interface Offer {
  eyebrow: string
  name: string
  system: string
  summary: string
  includesLabel: string
  includes: string[]
  listPrice: string
  price: string
  savings: string
  cta: string
  footnote: string
}

export const offer: Offer = {
  eyebrow: 'Oferta especial de lanzamiento',
  name: 'Atlas Visual de Instrumentación Quirúrgica',
  system: 'Sistema Completo — 6 Tomos + 5 Bonos',
  summary:
    'Recibes el Atlas de +150 instrumentos, instrumental por especialidad, mesas quirúrgicas, suturas y agujas, bioseguridad, simulador de práctica y los 5 bonos completos.',
  includesLabel: 'Además:',
  includes: ['PDF imprimible', 'Acceso inmediato', 'Pago único', 'Acceso de por vida'],
  listPrice: '$39.990 ARS',
  price: '$19.990',
  savings: '$20.000 ARS',
  cta: 'Quiero los 6 Tomos + 5 Bonos',
  footnote: 'Pago único · Acceso inmediato · Garantía de 7 días',
}

export const heroImage = 'https://cdn.magicpatterns.com/uploads/11G4dUWWdpBTdwysyASuGB/mockup-quirur.png'
